import { DESKTOP_MEDIA_QUERY } from "@/common/constants";
import { Database01Icon, HeartIcon } from "@/icons";
import { useMediaQuery } from "@/utils/hooks";
import { FormattedMessage, useIntl } from "react-intl";
import { graphql, useLazyLoadQuery } from "react-relay";
import { DatasetEmptyQuery as DatasetEmptyQueryType } from "./__generated__/DatasetEmptyQuery.graphql";
import { Carousel, CarouselControl, CarouselProvider } from "./Carousel";
import DatasetCard from "./DatasetCard";
import { MetaLayout } from "@/common/MetaLayout";

const DatasetEmptyQuery = graphql`
  query DatasetEmptyQuery {
    datasets(first: 5, filters: { order: TRENDING, sortDirection: ASCENDING }) {
      nodes {
        id
        ...DatasetCardFragment
      }
    }
  }
`;

interface Props {
  datasetId: ID;
}

export function DatasetEmpty(props: Props) {
  const intl = useIntl();
  const query = useLazyLoadQuery<DatasetEmptyQueryType>(
    DatasetEmptyQuery,
    {},
    { fetchPolicy: "store-or-network" },
  );
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const datasets = query.datasets.nodes.filter(
    ({ id }) => id !== props.datasetId,
  );

  return (
    <MetaLayout
      metaTitle={intl.formatMessage({
        defaultMessage: "Quantum Computing Dataset",
      })}
      metaDescription={intl.formatMessage({
        defaultMessage:
          "A dataset ready to download for algorithm development, QML workflows, and benchmarking.",
      })}
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col items-center justify-center gap-4 bg-brand-25 rounded-xl p-8 mt-4 lg:h-60 h-auto">
          <Database01Icon className="h-8 w-8 text-brand-500" />
          <div className="flex flex-col items-center justify-center gap-1">
            <p className="text-dxs font-semibold text-grey-950">
              <FormattedMessage defaultMessage="Looks like there is nothing to see yet" />
            </p>
            <p className="text-md font-normal text-grey-700">
              <FormattedMessage defaultMessage="Stay tuned for updates from the author." />
            </p>
          </div>
        </div>
        {datasets.length >= 3 && (
          <CarouselProvider
            itemCount={datasets.length}
            visible={isDesktop ? 3 : 1}
          >
            <div className="flex flex-col items-start gap-4">
              <div className="w-full flex flex-row items-center gap-1">
                <HeartIcon className="h-6 w-6 text-brand-500" />
                <div className="w-full flex flex-row items-center justify-between">
                  <p className="text-xl font-semibold text-grey-900">
                    <FormattedMessage defaultMessage="Datasets you might like" />
                  </p>
                  <CarouselControl />
                </div>
              </div>
              <Carousel
                items={datasets}
                renderItem={(item, index) => (
                  <DatasetCard key={index} dataset={item} />
                )}
              />
            </div>
          </CarouselProvider>
        )}
      </div>
    </MetaLayout>
  );
}
